import { Delete, Edit, LocationOn } from '@mui/icons-material'
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Container,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import MainLayout from '~/components/layouts/MainLayout'

import DefaultOrgLogo from '../assets/grey_isologo.png'
import ConfirmationModal from '../components/Deprecated_ConfirmationModal'
import { useAppSelector } from '../redux/hooks'
import {
  type Stakeholder,
  deleteStakeholder,
  getStakeholderPaginated,
} from '../services/stakeholder-services'
import { canAccess } from '../utils/role-utils'
import FormModal from './stakeholders/StakeholderFormModal'

export default function Stakeholders() {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const user = useAppSelector(state => state.user)
  const queryClient = useQueryClient()

  const [openFormModal, setOpenFormModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedStakeholder, setSelectedStakeholder] =
    useState<Stakeholder | null>(null)

  const toggleFormModal = (stakeholder: Stakeholder | null) => {
    setSelectedStakeholder(stakeholder)
    setOpenFormModal(!openFormModal)
  }

  const toggleDeleteModal = (stakeholder: Stakeholder | null) => {
    setSelectedStakeholder(stakeholder)
    setOpenDeleteModal(!openDeleteModal)
  }

  const stakeholdersQuery = useInfiniteQuery({
    queryKey: ['stakeholders'],
    queryFn: async args => {
      const pageParam: number = args.pageParam ?? 0
      return await getStakeholderPaginated(pageParam)
    },
    select: data => {
      return {
        ...data,
        pages: data.pages.flatMap(
          page => page.data.data.listStakeholder.content,
        ),
      }
    },
    getNextPageParam: (lastPage, pages) => {
      const { totalPages } = lastPage.data.data
      if (totalPages === 0) return null
      return totalPages === pages.length ? null : pages.length
    },
  })

  useEffect(() => {
    if (stakeholdersQuery.isError) {
      enqueueSnackbar(t('views.stakeholder.load.error'), { variant: 'error' })
    }
  }, [enqueueSnackbar, stakeholdersQuery.isError, t])

  const deleteMutation = useMutation({
    mutationFn: deleteStakeholder,
    onSuccess: () => {
      enqueueSnackbar(t('views.stakeholder.delete.success'), {
        variant: 'success',
      })
      void queryClient.invalidateQueries(['stakeholders'])
      toggleDeleteModal(null)
    },
    onError: () => {
      enqueueSnackbar(t('views.stakeholder.delete.error'), {
        variant: 'error',
      })
    },
  })

  const handleDeleteStakeholder = () => {
    if (!selectedStakeholder) return
    deleteMutation.mutate(selectedStakeholder.id)
  }

  const canWrite = canAccess(user, 'hubs', 'write')

  return (
    <MainLayout>
      <Box sx={{ minHeight: '100vh' }}>
        <FormModal
          open={openFormModal}
          key={selectedStakeholder?.id}
          afterSubmit={() => {
            void queryClient.invalidateQueries(['stakeholders'])
          }}
          stakeholder={selectedStakeholder}
          toggleModal={() => {
            toggleFormModal(null)
          }}
        />

        <ConfirmationModal
          title={t('views.stakeholder.delete.confirmTitle')}
          subtitle={t('views.stakeholder.delete.confirmSubTitle')}
          cancelButtonText={t('general.no')}
          continueButtonText={t('general.yes')}
          onClose={() => {
            toggleDeleteModal(null)
            void queryClient.invalidateQueries(['stakeholders'])
          }}
          loading={deleteMutation.isLoading}
          open={openDeleteModal}
          confirmAction={handleDeleteStakeholder}
        />
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                toggleFormModal(null)
              }}
              disabled={!canWrite}
            >
              {t('views.stakeholder.addStakeholder')}
            </Button>
          </Box>
          <Grid container spacing={2}>
            {stakeholdersQuery.isFetching &&
              Array.from({ length: 12 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Skeleton variant="rectangular" height={400} />
                </Grid>
              ))}

            {stakeholdersQuery.isSuccess &&
              stakeholdersQuery.data.pages.map(stakeholder => (
                <Grid item xs={12} sm={6} md={3} key={stakeholder.id}>
                  <StakeholderCard
                    stakeholder={stakeholder}
                    onEdit={toggleFormModal}
                    onDelete={toggleDeleteModal}
                  />
                </Grid>
              ))}
          </Grid>
        </Container>
      </Box>
    </MainLayout>
  )
}

interface StakeholderCardProps {
  stakeholder: Stakeholder

  onEdit: (stakeholder: Stakeholder) => void
  onDelete: (stakeholder: Stakeholder) => void
}

function StakeholderCard({
  stakeholder,
  onEdit,
  onDelete,
}: StakeholderCardProps) {
  const { t } = useTranslation()
  const user = useAppSelector(state => state.user)

  const canWrite = canAccess(user, 'hubs', 'write')
  const canDelete = canAccess(user, 'hubs', 'delete')

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            sx={{
              bgcolor: theme => theme.palette.primary.main,
            }}
          >
            <LocationOn />
          </Avatar>
        }
        title={<Typography color="primary">{stakeholder.name}</Typography>}
        subheader={
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={stakeholder.country}
          />
        }
      />
      <CardContent>
        <CardMedia
          component="img"
          src={stakeholder.logoUrl ?? DefaultOrgLogo}
          alt={stakeholder.name}
          sx={{
            height: 130,
            width: 130,
            margin: 'auto',
            borderRadius: 2,
            objectFit: 'contain',
          }}
        />
        <Typography variant="body2" color="textSecondary">
          {stakeholder.description}
        </Typography>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: 'space-around',
          mt: 'auto',
        }}
      >
        {canWrite && (
          <Button
            size="small"
            color="primary"
            variant="outlined"
            fullWidth
            startIcon={<Edit />}
            onClick={() => {
              onEdit(stakeholder)
            }}
          >
            {t('general.edit')}
          </Button>
        )}
        {canDelete && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            fullWidth
            startIcon={<Delete />}
            onClick={() => {
              onDelete(stakeholder)
            }}
          >
            {t('general.delete')}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}
